/**
 * RecommendedBooksScreen
 * 
 * Shows all recommended books for the user
 * Uses the same template as CategoryScreen
 */

import React, { useEffect, useState } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Pressable,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { Text, Theme, BookCard } from '../../components/ui';
import { FireBook } from '../../models/FirestoreModels';
import { auth } from '../../config/firebase';
import { PrivateProfileService } from '../../services/privateProfileService';
import { BookRepository } from '../../repositories/bookRepository';
import { getShortTitle, getShortDescription } from '../../utils/bookHelpers';

const RecommendedBooksScreen: React.FC = () => {
  const navigation = useNavigation();
  
  const [books, setBooks] = useState<FireBook[]>([]);
  const [loading, setLoading] = useState(true);

  // Load recommended books from mentor chat history
  useEffect(() => {
    const loadRecommendedBooks = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          setLoading(false);
          return;
        }

        // Get user profile
        const result = await PrivateProfileService.getProfile(user.uid);
        if (!result.success || !result.data) {
          setLoading(false);
          return;
        }

        const profile = result.data;

        // Get favorited books to filter out
        const booksProgress = profile.booksProgress || {};
        const favoriteIds = Object.entries(booksProgress)
          .filter(([_, progress]) => progress.isFavorite === true)
          .map(([bookId]) => bookId.split('_')[0]); // Get base book ID without language code

        // Get book recommendations from currentRecommendations (updated by chat)
        const recommendedBooks = profile.currentRecommendations?.recommendedBooks || [];
        
        if (recommendedBooks.length === 0) {
          setLoading(false);
          return;
        }

        // Fetch book data for all recommended book IDs (not limited to 10 here)
        const bookIdsToFetch = recommendedBooks
          .map(rec => rec.bookId)
          .filter(bookId => !favoriteIds.includes(bookId)); // Exclude favorited

        if (bookIdsToFetch.length === 0) {
          setLoading(false);
          return;
        }

        // Batch fetch all books (uses cache)
        const booksResult = await BookService.getBooksById(
          bookIdsToFetch, 
          profile.languageCode || 'en'
        );
        const fetchedBooks = booksResult.success && booksResult.data 
          ? booksResult.data 
          : [];

        console.log('[RecommendedBooksScreen] Loaded', fetchedBooks.length, 'books from mentor recommendations');
        setBooks(fetchedBooks);
      } catch (error) {
        console.error('[RecommendedBooksScreen] Error loading books:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRecommendedBooks();
  }, []);

  const handleBookPress = (book: FireBook) => {
    const bookId = book.id.includes('_') ? book.id.split('_')[0] : book.id;
    navigation.navigate('BookDetail' as never, { 
      bookId, 
      languageCode: book.languageCode || 'en'
    } as never);
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={Theme.colors.primary} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Pressable 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={Theme.colors.textPrimary} />
        </Pressable>
        <Text style={styles.headerTitle}>For you</Text>
        <View style={styles.headerSpacer} />
      </View>

      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {books.length > 0 ? (
          <View style={styles.booksList}>
            {books.map((book) => {
              const shortTitle = getShortTitle(book.title);
              const description = getShortDescription(book.introduction, 100);
              
              return (
                <BookCard
                  key={book.id}
                  title={shortTitle}
                  author={book.author}
                  coverUrl={book.coverImageUrl || book.coverImageFullUrl}
                  insightsCount={book.insightIds?.length || book.statistics?.insights}
                  mentorNote={description}
                  variant="compact"
                  onPress={() => handleBookPress(book)}
                  style={styles.bookCard}
                />
              );
            })}
          </View>
        ) : (
          <View style={styles.emptyState}>
            <Ionicons name="book-outline" size={48} color={Theme.colors.textSecondary} />
            <Text style={styles.emptyTitle}>No Recommendations Yet</Text>
            <Text style={styles.emptyDescription}>
              Chat with your mentor to get personalized book recommendations.
            </Text>
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Theme.spacing.lg,
    paddingVertical: Theme.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.08)',
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
    flex: 1,
    textAlign: 'center',
  },
  headerSpacer: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: Theme.spacing.xl,
    paddingTop: Theme.spacing.xl,
    paddingBottom: Theme.spacing.xxxl,
  },
  booksList: {
    gap: Theme.spacing.md,
  },
  bookCard: {
    marginBottom: Theme.spacing.sm,
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: Theme.spacing.xxxxl,
    paddingHorizontal: Theme.spacing.xl,
  },
  emptyTitle: {
    fontSize: 20,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
    marginTop: Theme.spacing.lg,
    marginBottom: Theme.spacing.sm,
  },
  emptyDescription: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
    textAlign: 'center',
  },
});

export default RecommendedBooksScreen;

